import { useState } from 'react';
import { all } from '@/api/doc/document-class';
import {
	DocumentType,
	DocumentTypeSchema,
	update,
} from '@/api/doc/document-type';
import { Button } from '@/components/ui/button';
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Edit } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';

export function UpdateDocumentType({
	documentType,
}: {
	documentType: DocumentType;
}) {
	const { data } = useQuery({
		queryFn: () => all({ includedExpired: false }),
		queryKey: ['document-classes'],
	});
	const [open, setOpen] = useState(false);
	const queryClient = useQueryClient();

	const { mutate } = useMutation({
		mutationFn: (updatedDocument: DocumentType) => update(updatedDocument),
		onSuccess: () => {
			toast.success(`Success! The document type has been successfully updated.`, {
				dismissible: true,
			});
			setOpen(false);
			queryClient.invalidateQueries({ queryKey: ['document-types'] });
		},
		onError: () => {
			toast.error(`Error! The document type could not be updated.`, {
				dismissible: true,
			});
		},
	});

	const form = useForm<DocumentType>({
		resolver: zodResolver(DocumentTypeSchema),
		defaultValues: documentType,
		mode: 'all',
	});

	const deletedDescription: string = !documentType.deletedOn
		? 'Toggle on to delete this document type.'
		: 'Toggle off to not delete this document type.';

	const deletedLabel: string = !documentType.deletedOn ? 'Delete?' : 'Deleted';

	const onSubmit: SubmitHandler<DocumentType> = (values: DocumentType) =>
		mutate(values);

	form.watch();
	return (
		<div>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button className="m-auto" variant="ghost" onClick={() => setOpen(true)}>
							<Edit size={16} />
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						<p>Update document type</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>

			<Sheet open={open} onOpenChange={(open) => setOpen(open)}>
				<SheetContent className="overflow-scroll">
					<SheetHeader>
						<SheetTitle>Update Document Type</SheetTitle>
					</SheetHeader>
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
							<FormField
								control={form.control}
								name="name"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Document Type Name</FormLabel>
										<FormControl>
											<Input {...field} placeholder="Document type name" />
										</FormControl>
										<FormDescription>Name the document type</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="documentClass.id"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Document Class</FormLabel>
										<Select onValueChange={field.onChange} value={field.value ?? ''}>
											<FormControl>
												<SelectTrigger>
													<SelectValue placeholder="Please select a document class." />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{data!.map((documentClass) => (
													<SelectItem
														key={documentClass.id}
														value={String(documentClass.id)}
													>
														{documentClass.name}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<FormDescription>
											Select a document class to associate with this document type.
										</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="deletedOn"
								render={({ field }) => (
									<FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
										<div className="space-y-0.5">
											<FormLabel>{deletedLabel}</FormLabel>
											<FormDescription>{deletedDescription}</FormDescription>
										</div>
										<FormControl>
											<Switch
												checked={!!field.value}
												onCheckedChange={() =>
													form.setValue('deletedOn', field.value ? null : new Date())
												}
											/>
										</FormControl>
									</FormItem>
								)}
							/>
							<Button
								type="submit"
								disabled={!form.formState.isValid || form.formState.isSubmitting}
							>
								{form.formState.isSubmitting ? 'Updating...' : 'Update'}
							</Button>
						</form>
					</Form>
				</SheetContent>
			</Sheet>
		</div>
	);
}
